import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "components/v2/FormField";
import useRequestSupport from "api/CommandCenterResource/useRequestSupport";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import {
	FiCheck,
	FiHeadphones,
	FiMail,
	FiMessageSquare,
	FiChevronDown,
	FiChevronUp,
} from "react-icons/fi";
import { useState } from "react";

const Support = () => {
	const [expandedCards, setExpandedCards] = useState({});

	const toggleCard = (index) => {
		setExpandedCards((prev) => ({
			...prev,
			[index]: !prev[index],
		}));
	};

	const initialValues = {
		name: "",
		email: "",
		query: "",
		category: "",
	};

	const validationSchema = Yup.object({
		name: Yup.string().optional(),
		email: Yup.string()
			.email("Invalid email format")
			.required("Your email is required"),
		query: Yup.string().required("Enter your enquiry or issue"),
		category: Yup.string().required("Select a category"),
	});

	const { mutateAsync, isLoading, isSuccess } = useRequestSupport();

	const onSubmit = async (data) => {
		await mutateAsync(data);
	};

	const supportFeatures = [
		{
			icon: (
				<FiHeadphones className="w-8 h-8 md:w-12 md:h-12 text-akgreener mb-4" />
			),
			title: "24/7 Support",
			description:
				"Our team is always ready to assist you with any questions or concerns.",
		},
		{
			icon: <FiMail className="w-8 h-8 md:w-12 md:h-12 text-akgreener mb-4" />,
			title: "Quick Response",
			description: "We aim to respond to all inquiries within 24 hours.",
		},
		{
			icon: (
				<FiMessageSquare className="w-8 h-8 md:w-12 md:h-12 text-akgreener mb-4" />
			),
			title: "Expert Help",
			description: "Get assistance from our knowledgeable support team.",
		},
	];

	return (
		<Container>
			<div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
				{/* Hero Section */}
				<div className="bg-akgreener text-white pt-16 pb-24">
					<PaddingX>
						<div className="max-w-3xl mx-auto text-center">
							<h1 className="text-4xl font-bold mb-6">
								How can we help you today?
							</h1>
							<p className="text-xl opacity-90">
								We're here to provide you with the best support experience. Let
								us know how we can assist you.
							</p>
						</div>
					</PaddingX>
				</div>

				{/* Support Features */}
				<div className="px-4 -mt-16">
					<PaddingX>
						<div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-8 max-w-6xl mx-auto">
							{supportFeatures.map((feature, index) => (
								<div
									key={index}
									className="bg-white rounded-lg shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden "
								>
									<button
										onClick={() => toggleCard(index)}
										className="w-full p-6 flex items-center justify-between md:block  md:cursor-text"
									>
										<div className="flex gap-x-2 items-center md:block">
											<div className="md:block hidden">{feature.icon}</div>
											<div className="h-8 md:hidden text-akgreener">
												{feature.icon}
											</div>
											<h3 className="text-xl font-semibold text-left">
												{feature.title}
											</h3>
										</div>
										<div className="md:hidden">
											{expandedCards[index] ? (
												<FiChevronUp size={24} />
											) : (
												<FiChevronDown size={24} />
											)}
										</div>
									</button>
									<div
										className={`px-6 pb-6 transition-all duration-300 ease-in-out ${
											expandedCards[index] ? "block" : "hidden md:block"
										}`}
									>
										<p className="text-gray-600">{feature.description}</p>
									</div>
								</div>
							))}
						</div>
					</PaddingX>
				</div>

				{/* Form Section */}
				<div className="py-12">
					<PaddingX>
						<Formik
							initialValues={initialValues}
							validationSchema={validationSchema}
							onSubmit={onSubmit}
						>
							{({ values }) => (
								<>
									{isSuccess ? (
										<div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-sm border border-gray-100">
											<div className="flex flex-col items-center gap-6">
												<span className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
													<FiCheck
														size={40}
														className="text-akgreener"
													/>
												</span>

												<h2 className="text-2xl font-bold text-center">
													Thank you for contacting us!
												</h2>

												<p className="text-center text-gray-600">
													We understand the importance of timely assistance, and
													we will do our best to get back to you as soon as
													possible. In the meantime, if you have any additional
													information to share or questions to ask, please don't
													hesitate to reach out to us.
												</p>

												<p className="font-semibold text-center text-akgreener">
													Look out for our reply to your email ({values.email})
												</p>
											</div>
										</div>
									) : (
										<Form className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-sm border border-gray-100">
											<div className="mb-8">
												<h2 className="text-2xl font-bold mb-4">
													Contact Support
												</h2>
												<p className="text-gray-600">
													Please fill out the form below and we'll get back to
													you as soon as possible.
												</p>
											</div>

											<div className="space-y-6">
												<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
													<FormField
														type="text"
														name="name"
														label="Your name (Optional)"
														className="bg-gray-50"
													/>

													<FormField
														type="email"
														name="email"
														label="Your email*"
														className="bg-gray-50"
													/>
												</div>

												<FormField
													label="Select inquiry category*"
													type="select"
													name="category"
													options={[
														{ value: "payments", label: "Payments" },
														{
															value: "navigation",
															label: "Platform navigation",
														},
														{ value: "registration", label: "Registration" },
														{
															value: "search",
															label: "Searching for resources",
														},
														{
															value: "feature_suggestion",
															label: "Feature suggestion",
														},
														{
															value: "content_suggestion",
															label: "Suggestion for content",
														},
														{ value: "other", label: "Other" },
													]}
													className="bg-gray-50"
												/>

												<FormField
													type="textarea"
													name="query"
													label="How can we help you*"
													className="bg-gray-50"
													rows={5}
												/>

												<button
													type="submit"
													className="w-full md:w-auto rounded-md py-3 px-6 flex items-center justify-center gap-2 bg-akgreener text-white hover:bg-green-700 transition-colors duration-200"
												>
													{isLoading ? (
														<>
															<AiOutlineLoading3Quarters
																size={20}
																className="animate-spin"
															/>
															Submitting...
														</>
													) : (
														"Submit Request"
													)}
												</button>
											</div>
										</Form>
									)}
								</>
							)}
						</Formik>
					</PaddingX>
				</div>
			</div>
		</Container>
	);
};

export default Support;
